/* eslint-disable react-compiler/react-compiler */

import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsDown, ChevronsUp } from 'lucide-react'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { ButtonSelectFormField } from 'src/shared/components/ui/forms/form-fields/button-select'
import { CheckboxFormField } from 'src/shared/components/ui/forms/form-fields/checkbox'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { PriceRangeFormField } from 'src/shared/components/ui/forms/form-fields/price-range'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import {
  propertyTypeMap,
  propertyTypeSchema,
  loanPurposeMap,
  loanPurposeSchema,
  type LoanPurpose,
  loanTypeMap,
  getLoanTypeSchema,
  refinanceReasonSchema,
  refinanceReasonMap,
  employmentStatusSchema,
  employmentStatusMap,
  creditScoreSchema,
  creditScoreMap,
} from 'src/shared/schemas/referrals'
import { useEventStore } from 'src/shared/store/event-store'

const baseSchema = z.object({
  loanPurpose: loanPurposeSchema,
})

const purchaseSchema = z.object({
  priceRange: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  propertyType: propertyTypeSchema,
  downPaymentEstimate: z.number().min(1).optional(), // has not sure checkbox
  // Optional fields
  incomeEstimate: z.number().min(1).optional(),
  assetsEstimate: z.number().min(0).optional(),
  isPreApproved: z.boolean().optional(),
  employmentStatus: employmentStatusSchema.optional(),
  loanType: getLoanTypeSchema('purchase' as LoanPurpose).optional(),
  creditScoreEstimate: creditScoreSchema.optional(),
})

const refinanceSchema = z.object({
  refinanceReason: refinanceReasonSchema,
  exactPrice: z.number().min(1),
  propertyType: propertyTypeSchema,
  // Optional fields
  mortgageBalance: z.number().min(0).optional(),
  employmentStatus: employmentStatusSchema.optional(),
  loanType: getLoanTypeSchema('refinance' as LoanPurpose).optional(),
  creditScoreEstimate: creditScoreSchema.optional(),
})

const otherSchema = z.object({
  loanType: getLoanTypeSchema('other' as LoanPurpose),
  priceRange: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
})

// Combined schema with discriminated union
const loanOfficerSchema = z.discriminatedUnion('loanPurpose', [
  baseSchema
    .extend({
      loanPurpose: z.literal('purchase'),
    })
    .merge(purchaseSchema),
  baseSchema
    .extend({
      loanPurpose: z.literal('refinance'),
    })
    .merge(refinanceSchema),
  baseSchema
    .extend({
      loanPurpose: z.literal('other'),
    })
    .merge(otherSchema),
])

type LoanOfficerFormData = z.infer<typeof loanOfficerSchema>

export const LoanOfficerInfo: FC = () => {
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const [showOptional, setShowOptional] = useState(false)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  // Type-specific default values
  const typeSpecificDefaults = useMemo(
    () => ({
      purchase: {
        loanPurpose: 'purchase' as LoanPurpose,

        priceRange: {
          min: referralFormData.priceRange?.min || selectedReferral?.priceRange?.min || 100000,
          max: referralFormData.priceRange?.max || selectedReferral?.priceRange?.max || 450000,
        },
        propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
        downPaymentEstimate: referralFormData.downPaymentEstimate || selectedReferral?.downPaymentEstimate || undefined, // has not sure checkbox
        // Optional fields
        incomeEstimate: referralFormData.incomeEstimate || selectedReferral?.incomeEstimate || undefined,
        assetsEstimate: referralFormData.assetsEstimate || selectedReferral?.assetsEstimate || undefined,
        isPreApproved: referralFormData.isPreApproved || selectedReferral?.isPreApproved || undefined,
        employmentStatus: referralFormData.employmentStatus || selectedReferral?.employmentStatus || undefined,
        loanType: referralFormData.loanType || selectedReferral?.loanType || undefined,
        creditScoreEstimate: referralFormData.creditScoreEstimate || selectedReferral?.creditScoreEstimate || undefined,
      },
      refinance: {
        loanPurpose: 'refinance' as LoanPurpose,
        refinanceReason: referralFormData.refinanceReason || selectedReferral?.refinanceReason || undefined,
        exactPrice: referralFormData.exactPrice || selectedReferral?.exactPrice || undefined,
        propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
        // Optional fields
        mortgageBalance: referralFormData.mortgageBalance || selectedReferral?.mortgageBalance || undefined,
        employmentStatus: referralFormData.employmentStatus || selectedReferral?.employmentStatus || undefined,
        loanType: referralFormData.loanType || selectedReferral?.loanType || undefined,
        creditScoreEstimate: referralFormData.creditScoreEstimate || selectedReferral?.creditScoreEstimate || undefined,
      },
      other: {
        loanPurpose: 'other' as LoanPurpose,
        loanType: referralFormData.loanType || selectedReferral?.loanType || undefined,
        priceRange: {
          min: referralFormData.priceRange?.min || selectedReferral?.priceRange?.min || 100000,
          max: referralFormData.priceRange?.max || selectedReferral?.priceRange?.max || 450000,
        },
      },
    }),
    [referralFormData, selectedReferral],
  )

  const form = useForm<LoanOfficerFormData>({
    resolver: zodResolver(loanOfficerSchema),
    mode: 'onSubmit',
    defaultValues: {
      ...(selectedReferral?.loanPurpose
        ? typeSpecificDefaults[selectedReferral.loanPurpose]
        : typeSpecificDefaults.purchase),
    },
  })

  // Reset form fields when client type changes
  const loanPurpose = form.watch('loanPurpose')

  useEffect(() => {
    const newValues = {
      ...form.getValues(),
      ...typeSpecificDefaults[loanPurpose],
    }
    form.reset(newValues as LoanOfficerFormData)
  }, [loanPurpose, form, typeSpecificDefaults])

  const onSubmit = async (data: LoanOfficerFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_loan_officer_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 space-y-6">
        <ButtonSelectFormField
          control={form.control}
          name="loanPurpose"
          label="Purpose"
          allowDeselect={false}
          gridCols={3}
          options={loanPurposeMap}
          required
        />

        {/* Purpose-specific fields */}
        {loanPurpose === 'purchase' && (
          <>
            <PriceRangeFormField control={form.control} name="priceRange" label="Purchase Price" required />
            <SelectFormField
              control={form.control}
              name="propertyType"
              label="Property Type"
              options={propertyTypeMap}
              required
            />
            <InputFormField
              control={form.control}
              name="downPaymentEstimate"
              label="Down Payment Estimate"
              floatingLabel={false}
              type="currency"
              required
              checkbox={{ label: 'Not sure', defaultChecked: false }}
            />
          </>
        )}

        {loanPurpose === 'refinance' && (
          <>
            <SelectFormField
              control={form.control}
              name="refinanceReason"
              label="Refinance Reason"
              options={refinanceReasonMap}
              required
            />
            <InputFormField
              control={form.control}
              name="exactPrice"
              label="Property Value"
              type="currency"
              floatingLabel={false}
              required
            />
            <SelectFormField
              control={form.control}
              name="propertyType"
              label="Property Type"
              options={propertyTypeMap}
              required
            />
          </>
        )}

        {loanPurpose === 'other' && (
          <>
            <SelectFormField
              required
              control={form.control}
              name="loanType"
              label="Loan Type"
              options={loanTypeMap[loanPurpose]}
            />
            <PriceRangeFormField control={form.control} name="priceRange" label="Purchase Price" required />
          </>
        )}

        {(loanPurpose === 'refinance' || loanPurpose === 'purchase') && (
          <div className="flex w-full justify-center">
            <Button
              variant="link"
              type="button"
              onClick={() => setShowOptional(!showOptional)}
              className="flex items-center justify-center gap-2"
            >
              {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
              {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
            </Button>
          </div>
        )}

        {showOptional && loanPurpose === 'purchase' && (
          <>
            <InputFormField
              control={form.control}
              name="incomeEstimate"
              label="Income"
              floatingLabel={false}
              type="currency"
            />
            <InputFormField
              control={form.control}
              name="assetsEstimate"
              label="Assets"
              floatingLabel={false}
              type="currency"
            />
            <CheckboxFormField control={form.control} name="isPreApproved" label="Pre-Approved" />
            <SelectFormField
              control={form.control}
              name="employmentStatus"
              label="Employment Status"
              options={employmentStatusMap}
            />
            <SelectFormField
              control={form.control}
              name="loanType"
              label="Loan Type"
              options={loanTypeMap[loanPurpose]}
            />
            <SelectFormField
              control={form.control}
              name="creditScoreEstimate"
              label="Credit Score"
              options={creditScoreMap}
            />
          </>
        )}

        {showOptional && loanPurpose === 'refinance' && (
          <>
            <InputFormField
              control={form.control}
              name="mortgageBalance"
              label="Mortgage Balance"
              floatingLabel={false}
              type="currency"
            />
            <SelectFormField
              control={form.control}
              name="employmentStatus"
              label="Employment Status"
              options={employmentStatusMap}
            />
            <SelectFormField
              control={form.control}
              name="loanType"
              label="Loan Type"
              options={loanTypeMap[loanPurpose]}
            />
            <SelectFormField
              control={form.control}
              name="creditScoreEstimate"
              label="Credit Score"
              options={creditScoreMap}
            />
          </>
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
